import { useCallback, useRef } from "react";

// Куда складываем scrollTop, когда контейнер размонтируется. Module-level
// Map переживает unmount/remount компонентов в рамках одной сессии — refresh
// страницы её, разумеется, обнуляет (что и ожидается).
const cache = new Map<string, number>();

// Хук для сохранения и восстановления позиции скролла внутри произвольного
// элемента. Используется как callback ref: ставим на скроллящийся div, при
// первом mount элемент скроллится туда, где был, при unmount — текущая
// позиция кладётся в кеш под `key`.
//
// Почему callback ref, а не useEffect: целевой div появляется в DOM не сразу
// (его рендер ждёт `screen.data`), и обычный effect успел бы отработать до
// монтирования с ref.current=null. Callback ref вызывается ровно тогда,
// когда DOM-нода привязывается или отвязывается.
export function useScrollRestore<T extends HTMLElement>(
  key: string,
): (el: T | null) => void {
  const elRef = useRef<T | null>(null);

  return useCallback(
    (el: T | null) => {
      if (el) {
        const saved = cache.get(key);
        if (saved != null) el.scrollTop = saved;
      } else if (elRef.current) {
        const top = elRef.current.scrollTop;
        if (top > 0) cache.set(key, top);
        else cache.delete(key);
      }
      elRef.current = el;
    },
    [key],
  );
}
